(() => {
  "use strict";

  if (document.documentElement.dataset.looomHeroScriptLoaded === "true") {
    return;
  }

  document.documentElement.dataset.looomHeroScriptLoaded = "true";

  const SECTION_SELECTOR = ".js-looom-hero";
  const VIDEO_SELECTOR = ".looom-hero__video";
  const initializedSections = new WeakSet();
  const instances = [];
  const instanceBySection = new WeakMap();
  let rafId = 0;

  // Only re-seek when the target has moved by at least this many seconds.
  // Avoids issuing redundant sub-frame seeks that never paint.
  const MIN_TIME_DELTA_TO_SEEK = 1 / 60;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const scheduleFrame = () => {
    if (rafId || !instances.some((instance) => instance.isNearViewport)) {
      return;
    }

    rafId = window.requestAnimationFrame(() => {
      rafId = 0;

      for (let index = instances.length - 1; index >= 0; index -= 1) {
        const instance = instances[index];

        if (!document.documentElement.contains(instance.section)) {
          visibilityObserver?.unobserve(instance.section);
          instances.splice(index, 1);
          continue;
        }

        if (instance.isNearViewport) {
          instance.update();
        }
      }
    });
  };

  class LoomHeroVideoScrub {
    constructor(section) {
      this.section = section;
      this.video = section.querySelector(VIDEO_SELECTOR);
      this.duration = 0;
      this.progress = 0;
      this.isMetadataReady = false;
      // The time we want to be at, updated freely on every frame.
      this.targetTime = 0;
      // The time we last handed to the decoder.
      this.appliedTime = -1;
      // True while the decoder is mid-seek; we defer new seeks until it finishes.
      this.isSeeking = false;
      this.isNearViewport = false;
      this.hasRequestedBuffer = false;
      this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (!this.video) {
        return;
      }

      this.onMetadataLoaded = this.onMetadataLoaded.bind(this);
      this.onSeeked = this.onSeeked.bind(this);

      this.video.autoplay = false;
      this.video.loop = false;
      this.video.controls = false;
      this.video.muted = true;
      this.video.playsInline = true;

      this.video.addEventListener("seeked", this.onSeeked);

      if (
        this.video.readyState >= 1 &&
        Number.isFinite(this.video.duration) &&
        this.video.duration > 0
      ) {
        this.onMetadataLoaded();
      } else {
        this.video.addEventListener("loadedmetadata", this.onMetadataLoaded, {
          once: true,
        });
      }

    }

    setNearViewport(isNearViewport) {
      this.isNearViewport = isNearViewport;

      if (!isNearViewport || this.prefersReducedMotion) {
        return;
      }

      if (!this.hasRequestedBuffer) {
        this.hasRequestedBuffer = true;
        this.video.preload = "auto";
        this.isMetadataReady = false;
        this.appliedTime = -1;
        this.isSeeking = false;
        this.video.addEventListener("loadedmetadata", this.onMetadataLoaded, {
          once: true,
        });
        this.video.load();
      }

      this.update();
    }

    onMetadataLoaded() {
      if (!Number.isFinite(this.video.duration) || this.video.duration <= 0) {
        return;
      }

      this.duration = this.video.duration;
      this.isMetadataReady = true;
      this.update();
    }

    // When a seek completes, immediately chase the latest target if it moved
    // while the decoder was busy. This keeps us at the newest scroll position
    // without ever queuing more than one seek at a time.
    onSeeked() {
      this.isSeeking = false;
      this.applyTarget();
    }

    update() {
      if (!this.isNearViewport || this.prefersReducedMotion) {
        return;
      }

      this.progress = this.getSectionProgress();

      if (!this.isMetadataReady || this.duration <= 0) {
        return;
      }

      this.targetTime = this.duration * this.progress;
      this.applyTarget();
    }

    applyTarget() {
      // Never overlap seeks: a new one issued mid-decode is what causes stutter.
      if (this.isSeeking) {
        return;
      }

      const nextTime = clamp(this.targetTime, 0, this.duration);

      if (Math.abs(nextTime - this.appliedTime) < MIN_TIME_DELTA_TO_SEEK) {
        return;
      }

      this.appliedTime = nextTime;
      this.isSeeking = true;

      try {
        this.video.currentTime = nextTime;
      } catch (error) {
        this.isSeeking = false;
      }
    }

    getSectionProgress() {
      const rect = this.section.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const totalTravel = rect.height + viewportHeight;

      if (totalTravel <= 0) {
        return 0;
      }

      const traveled = viewportHeight - rect.top;
      return clamp(traveled / totalTravel, 0, 1);
    }
  }

  const visibilityObserver = "IntersectionObserver" in window
    ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const instance = instanceBySection.get(entry.target);
          if (!instance) return;

          instance.setNearViewport(entry.isIntersecting);
        });

        scheduleFrame();
      },
      { rootMargin: "300px 0px" },
    )
    : null;

  const initSection = (section) => {
    if (!section || initializedSections.has(section)) {
      return;
    }

    initializedSections.add(section);

    const instance = new LoomHeroVideoScrub(section);
    if (!instance.video) {
      return;
    }

    instances.push(instance);
    instanceBySection.set(section, instance);

    if (visibilityObserver) {
      visibilityObserver.observe(section);
    } else {
      instance.setNearViewport(true);
    }
  };

  const initAllSections = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAllSections());
  } else {
    initAllSections();
  }

  window.addEventListener("scroll", scheduleFrame, { passive: true });
  window.addEventListener("resize", scheduleFrame, { passive: true });

  document.addEventListener("shopify:section:load", (event) => {
    if (!event.target) {
      return;
    }

    if (event.target.matches && event.target.matches(SECTION_SELECTOR)) {
      initSection(event.target);
      return;
    }

    initAllSections(event.target);
    scheduleFrame();
  });
})();
